import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Card } from "@/components/widgets/Paper";
import { useHover } from '../common/withHover';
import { EXPAND_FOOTNOTES_EVENT } from '../contents/CollapsedFootnotes';
import { hasCollapsedFootnotes } from '@/lib/betas';
import classNames from 'classnames';
import { parseDocumentFromString } from '@/lib/domParser';
import { usePostsPageContext } from '../posts/PostsPage/PostsPageContext';
import { sidenotesHiddenBreakpoint, RIGHT_COLUMN_WIDTH_WITH_SIDENOTES } from '../posts/PostsPage/constants';
import { useIsAboveBreakpoint } from '../hooks/useScreenWidth';
import { SideItem, useHasSideItemsSidebar } from '../contents/SideItems';
import { useDialog } from '../common/withDialog';
import { isRegularClick } from "@/components/posts/TableOfContents/TableOfContentsList";
import { isMobile } from '@/lib/utils/isMobile';
import ContentStyles from '../common/ContentStyles';
import FootnoteDialog from "./FootnoteDialog";
import SideItemLine from "../contents/SideItemLine";
import LWPopper from "../common/LWPopper";
import { ContentItemBody } from "../contents/ContentItemBody";
import { InteractionWrapper } from '../common/useClickableCell';
import type { ContentStyleType } from '@/components/common/ContentStylesValues';
import { useTheme } from '../themes/useTheme';
import { defineStyles, useStyles } from '../hooks/useStyles';
import { useFootnoteHTML } from './useFootnoteHTML';

export const footnotePreviewStyles = defineStyles("FootnotePreview", (theme: ThemeType) => ({
  hovercard: {
    padding: 16,
    ...theme.typography.body2,
    fontSize: "1.1rem",
    ...theme.typography.commentStyle,
    color: theme.palette.grey[800],
    maxWidth: 500,
    '& a': {
      color: theme.palette.primary.main,
    },
    
    "& .footnote-back-link": {
      display: "none",
    },
  },
  
  anchorHover: {
    border: `2px solid ${theme.palette.primary.dark}`,
    margin: -2,
    borderRadius: 2,
  },

  sidenote: {
    [sidenotesHiddenBreakpoint(theme)]: {
      display: "none",
    },
    "& .footnote-back-link, & a[href^=\"#fnref\"]": {
      display: "none",
    },

    width: RIGHT_COLUMN_WIDTH_WITH_SIDENOTES,
    padding: 12,
    
    "& .footnote-content": {
      width: "auto !important",
      maxWidth: "100%",
      
      "& ul:first-child, & ol:first-child": {
        marginTop: 0,
      },
    },
  },

  footnoteMobileIndicator: {
    display: "none",
    [sidenotesHiddenBreakpoint(theme)]: {
      display: "inline-block",
    },
  },
  
  lineColor: {
    background: theme.palette.sideItemIndicator.footnote,
  },

  sidenoteWithIndex: {
    display: "flex",
  },

  sidenoteIndex: {
    display: "inline-block",
    verticalAlign: "top",
    fontSize: 15,
    marginRight: 0,
    whiteSpace: "nowrap",
    lineHeight: "19px",

    color: theme.palette.greyAlpha(0.65),
    "$sidenoteHover &": {
      color: theme.palette.text.normal,
    },
  },
  
  sidenoteContent: {
    display: "inline-block",
    position: "relative",
    verticalAlign: "top",
    fontSize: 15,
    lineHeight: "19px",
    maxWidth: "100%",
    maxHeight: 200,
    overflow: "hidden",

    color: theme.palette.greyAlpha(0.65),
    "$sidenoteHover &": {
      color: theme.palette.text.normal,
    },
    "& li": {
      fontSize: "0.9em",
      lineHeight: "1.4em",
    },
  },
  
  overflowFade: {
    position: "absolute",
    top: 160,
    height: 40,
    width: "100%",
    background: `linear-gradient(0deg,${theme.palette.background.pageActiveAreaBackground},transparent)`,
  },
  
  sidenoteHover: {
    background: theme.palette.background.sidenoteBackground,
    borderRadius: 3,
    
    "& $sidenoteContent": {
      maxHeight: "unset",
    },
    "& $overflowFade": {
      display: "none",
    },
  },
}))

/**
 * Since footnotes can contain footnotes, by default we could have a side-item
 * for a footnote that contains itself, leading to infinite sidenotes (until a
 * React stack-depth limit is reached). Use a context provider to keep track of
 * what footnotes we're in, to prevent this.
 */
export const FootnoteAncestorsContext = React.createContext<string[]|null>(null);

const FootnotePreview = ({href, id, rel, contentStyleType="postHighlight", children}: {
  href: string,
  id?: string,
  rel?: string,
  contentStyleType?: ContentStyleType,
  children: React.ReactNode,
}) => {
  const classes = useStyles(footnotePreviewStyles);
  const { openDialog } = useDialog();
  const [disableHover, setDisableHover] = useState(false);
  const theme = useTheme();
  const minScreenWidthForTooltips = theme.breakpoints.values.sm;
  const { eventHandlers: anchorEventHandlers, hover: anchorHovered, anchorEl } = useHover({
    eventProps: {
      pageElementContext: "linkPreview",
      hoverPreviewType: "DefaultPreview",
      href,
    },
    getIsEnabled: () => {
      return !isMobile() && window.innerWidth >= minScreenWidthForTooltips;
    },
  });
  const { eventHandlers: sidenoteEventHandlers, hover: sidenoteHovered } = useHover();
  const footnoteAnchorRef = useRef<HTMLAnchorElement|null>(null);
  const eitherHovered = anchorHovered || sidenoteHovered;
  const memoizedEmptyArray = useMemo(() => [], []);
  const footnoteAncestors = useContext(FootnoteAncestorsContext) ?? memoizedEmptyArray;
  const newFootnoteAncestors = useMemo(() => [...footnoteAncestors, href], [footnoteAncestors, href])

  const footnoteHTML = useFootnoteHTML(href, footnoteAncestors);
  
  // TODO: Getting the footnote content from the DOM didn't necessarily work;
  // for example if the page was only showing an excerpt (with the rest hidden
  // behind a Read More), it won't be available, and might require a separate
  // network request to get the rest of the post/comment. Unfortunately in this
  // context, figuring out what that network request *is* is pretty complicated;
  // it could be anything with a content-editable field in it, and that
  // information isn't wired to pass through the hover-preview system.

  const onClick = useCallback((ev: React.MouseEvent) => {
    const isWideEnoughForTooltips = window.innerWidth >= minScreenWidthForTooltips;
    const openModalOnClick = isMobile() || !isWideEnoughForTooltips;
    if (isRegularClick(ev) && openModalOnClick && footnoteHTML !== null) {
      setDisableHover(true);
      // Anchor the dialog to the footnote reference in the text (also when
      // opened from the side-item indicator, which sits beside the same line).
      const anchorEl = footnoteAnchorRef.current;
      openDialog({
        name: "FootnoteDialog",
        contents: ({onClose}) => <FootnoteDialog
          onClose={onClose}
          footnoteHTML={footnoteHTML}
          anchorEl={anchorEl}
        />
      });
      ev.preventDefault();
    } else {
      window.dispatchEvent(new CustomEvent(EXPAND_FOOTNOTES_EVENT, {detail: href}));
    }
  }, [href, footnoteHTML, openDialog, minScreenWidthForTooltips]);
  
  const postPageContext = usePostsPageContext();
  const post = postPageContext?.fullPost ?? postPageContext?.postPreload;
  const sidenotesDisabledOnPost = post?.disableSidenotes;
  const screenIsWideEnoughForSidenotes = useIsAboveBreakpoint("lg");
  const hasSideItemsSidebar = useHasSideItemsSidebar();
  const sidenoteIsVisible = hasSideItemsSidebar && !sidenotesDisabledOnPost && screenIsWideEnoughForSidenotes;

  return (
    <span>
      {!sidenotesDisabledOnPost && footnoteHTML !== null &&
        <SideItem options={{offsetTop: -6}}>
          <div
            {...sidenoteEventHandlers}
            className={classNames(
              classes.sidenote,
              eitherHovered && classes.sidenoteHover
            )}
          >
            <FootnoteAncestorsContext.Provider value={newFootnoteAncestors}>
              <SidenoteDisplay
                footnoteHref={href}
                footnoteHTML={footnoteHTML}
                contentStyleType={contentStyleType}
              />
            </FootnoteAncestorsContext.Provider>
          </div>
          <span className={classes.footnoteMobileIndicator} onClick={onClick}>
            <SideItemLine colorClass={classes.lineColor}/>
          </span>
        </SideItem>
      }

      <a
        {...anchorEventHandlers}
        ref={footnoteAnchorRef}
        href={hasCollapsedFootnotes ? undefined : href}
        id={id}
        rel={rel}
        onClick={onClick}
        className={classNames(eitherHovered && classes.anchorHover)}
      >
        {children}
        {footnoteHTML !== null && !disableHover && <LWPopper
          open={anchorHovered && !sidenoteIsVisible}
          anchorEl={anchorEl}
          placement="bottom-start"
          allowOverflow
          flip
          clickable
        >
          <InteractionWrapper>
            <Card>
              <ContentStyles contentType={contentStyleType} className={classes.hovercard}>
                <ContentItemBody dangerouslySetInnerHTML={{__html: footnoteHTML || ""}} />
              </ContentStyles>
            </Card>
          </InteractionWrapper>
        </LWPopper>}
      </a>
    </span>
  );
}

const SidenoteDisplay = ({footnoteHref, footnoteHTML, contentStyleType}: {
  footnoteHref: string,
  footnoteHTML: string,
  contentStyleType: ContentStyleType,
}) => {
  const classes = useStyles(footnotePreviewStyles);
  const footnoteIndex = getFootnoteIndex(footnoteHref, footnoteHTML);

  return (
    <ContentStyles contentType={contentStyleType}>
      <span className={classes.sidenoteWithIndex}>
        {footnoteIndex !== null && <span className={classes.sidenoteIndex}>
          {footnoteIndex}{"."}
        </span>}
        <div className={classes.sidenoteContent}>
          <ContentItemBody dangerouslySetInnerHTML={{__html: footnoteHTML}} />
          <div className={classes.overflowFade} />
        </div>
      </span>
    </ContentStyles>
  );
}

function getFootnoteId(href: string, html: string): string|null {
  if (href.match(/^#fn.*/)) {
    return href.substring(3);
  } else {
    const { document: parsedFootnote } = parseDocumentFromString(html);
    const footnoteId = parsedFootnote
      .getElementsByClassName("footnote-back-link")
      ?.[0]
      ?.getAttribute("data-footnote-id")
    return footnoteId;
  }
}

/**
 * Given a footnote link (in the form of a link-anchor, and inner HTML for the
 * footnote reference), return a footnote-number (or null if we can't find one).
 * Because of different versions of the CkEditor footnote plugin, imported
 * posts, etc, we have several different strategies for doing this:
 *  - Find the footnote reference, and use the data-footnote-index prop
 *  - Find the footnote in the page footer, and if it's inside an <ol> tag, use
 *    its position within that tag's list of children
 * 
 * We specifically look for footnotes with an OL parent that has the "footnotes" class
 * to avoid using versions of the footnote that's present from the ckEditor edit form (present for quick-switching).
 */
function getFootnoteIndex(href: string, html: string): string|null {
  // Parse the footnote for its data-footnote-id, use that to find the footnote
  // reference (which is a span that wraps around the part of the footnote that
  // got hover-preview-ified), and take the data-footnote-index from that to
  // display.
  const footnoteId = getFootnoteId(href, html);
  if (!footnoteId) return null;

  const footnoteRef = document.getElementById("fnref"+footnoteId);
  const fromFootnoteIndexAttribute = footnoteRef?.getAttribute("data-footnote-index");
  
  if (fromFootnoteIndexAttribute) {
    return fromFootnoteIndexAttribute;
  }
  
  const allMatchingElements = Array.from(document.querySelectorAll(`#fn${footnoteId}`));
  
  // Try to find element with an OL parent with the "footnotes" class
  // This prevents using the version of the footnote from within quick-switch edit form that has a div parent instead of an ol
  const footnoteWithOlParent = allMatchingElements.find(el => 
    el.parentElement?.tagName === 'OL' &&
    (el.parentElement.classList.contains('footnotes')
      || el.parentElement.classList.contains('footnotes-list'))
  );
  
  if (footnoteWithOlParent) {
    const parentElement = footnoteWithOlParent.parentElement;
    
    const olStartAttr = parentElement?.getAttribute("start");
    const olStart = olStartAttr ? parseInt(olStartAttr) : 1;

    let numPrecedingLiElements = 0;
    for (let i=0; i<(parentElement?.children.length ?? 0); i++) {
      const elem = parentElement?.children.item(i);
      if (elem === footnoteWithOlParent) {
        break;
      }
      if (elem?.tagName === 'LI') {
        numPrecedingLiElements++;
      }
    }
    
    return (olStart + numPrecedingLiElements).toString();
  }
  
  return null;
}

export default FootnotePreview;


